import React, { useCallback, useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { CloseButton } from 'js/citations_dashboard_v2/CloseButton';
import { openLoginModal } from 'js/login_modal/LoginModalManager';
import { write } from 'js/core_webpack/arbitrary_events';

import { useExitIntent } from './useExitIntent';
import {
  ExitIntentDismissMethod,
  ExitIntentEventName,
  ExitIntentRelatedWork,
  LoswpExitIntentModalProps,
} from './types';

// Desktop exit-intent modal for the logged-out single work page: surfaces the top 3 related papers
// when the pointer leaves through the top of the viewport, at most once per session. Rendered only
// for the treatment arm of demo_day_2026_loswp_exit_intent -- SingleWorkController owns that gate,
// so control mounts nothing at all.
//

const DOWNLOAD_SOURCE = 'loswp--exit-intent-download-pdf-modal';
const VIEW_SOURCE = 'loswp--exit-intent-view-pdf';

// The copy overrides LoginButtons::Base applies by default, kept identical so the login modal reads
// the same however the visitor reached it.
const LOGIN_MODAL_TEXT = {
  buttonsPage: { header: 'Read your PDF for free' },
  emailEntryPage: { subheader: 'Sign up or log in to continue reading.' },
  emailLoginPage: { subheader: 'Log in to continue reading.' },
  emailRegistrationPage: { subheader: 'Sign up to continue reading.' },
};

const PromoDialog = styled(Modal)`
  & :global(.ds2-5-modal__container) {
    max-width: 720px;
    padding: var(--spacing-sm-32, 32px);
  }
  font-family: 'DM Sans';
`;

const Sheet = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const SheetCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0;
  right: 0;
  color: var(--text-primary, #141413);
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  margin-top: var(--spacing-xs-16, 16px);
`;

// Info on the left, CTAs in a row on the right.
const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-sm, 8px);

  // Narrow windows can't fit a title plus two buttons side by side; stack rather than squash the
  // CTAs. Desktop-only surface, so this is a small-laptop concern, not a phone one.
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  // Without this a long title refuses to wrap and pushes the CTAs out of the card.
  min-width: 0;
`;

const CardTitle = styled.p`
  font-weight: 600;
  font-size: 16px;
`;

const Center = styled.div`
  text-align: center;
`;

const CardMetadata = styled.div`
  color: var(--text-primary, #141413);
`;

const CardCtas = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  // The buttons keep their intrinsic width; the info column absorbs the slack.
  flex-shrink: 0;
`;

export const LoswpExitIntentModal: React.FC<LoswpExitIntentModalProps> = ({
  workId,
  abTestBucket,
  designVariant,
  showModal,
  relatedWorks,
}) => {
  const { triggered, reset } = useExitIntent();
  const [open, setOpen] = useState(false);
  // Tracked separately from `open` so control -- which never opens anything -- still records
  // exactly once.
  const [detected, setDetected] = useState(false);

  // Telemetry is best-effort and must never break the modal: a throw here would abort the
  // handler that called it. Same swallow pattern as analytics/metrics/tabActivityTracking.ts.
  const record = useCallback(
    (
      event: ExitIntentEventName,
      extra: {
        relatedWorkId?: number | null;
        position?: number | null;
        dismissMethod?: ExitIntentDismissMethod | null;
      } = {},
    ): void => {
      try {
        const sent = write('LoswpExitIntentEvent', {
          event,
          work_id: workId,
          ab_test_bucket: abTestBucket,
          related_work_id: extra.relatedWorkId ?? null,
          position: extra.position ?? null,
          dismiss_method: extra.dismissMethod ?? null,
        });
        if (sent && typeof sent.catch === 'function') sent.catch(() => {});
      } catch {
        // swallow — telemetry is never load-bearing
      }
    },
    [workId, abTestBucket],
  );

  // Recorded when exit intent fires, never on mount -- most bucketed visitors never trigger it at
  // all, so these stay true denominators.
  //
  // Exactly ONE event per arm, and they partition cleanly: control emits `exit_intent_detected`,
  // treatment emits `shown`. Together they are every exit-intent occurrence in the test, so the
  // arms' downstream signup rates divide straight down that line.
  //
  // They are directly comparable because both hang off the same `triggered` flag: useExitIntent
  // has already applied the top-of-viewport heuristic, the once-per-session guard and the
  // isModalPresent() suppression before either can fire. Neither arm sees a population the other
  // doesn't.
  useEffect(() => {
    if (!triggered || detected) return;
    setDetected(true);

    if (!showModal) {
      record('exit_intent_detected');
      return;
    }

    setOpen(true);
    record('shown');
  }, [triggered, detected, showModal, record]);

  const close = (dismissMethod: ExitIntentDismissMethod): void => {
    setOpen(false);
    reset();
    record('dismissed', { dismissMethod });
  };

  const onDownload = (work: ExitIntentRelatedWork, position: number): void => {
    record('download_pdf_click', { relatedWorkId: work.id, position });
    openLoginModal({
      postLoginRedirectUrl: work.url,
      clickTrackingContext: {
        source: DOWNLOAD_SOURCE,
        bucket: designVariant,
        entityId: work.id,
        extra: { source_work_id: workId, position },
      },
      configuredText: LOGIN_MODAL_TEXT,
      registrationParams: {
        user_source: 'exit_intent',
        ...(work.attachmentId ? { doc_id: work.attachmentId } : {}),
      },
    });
  };

  // Control mounts solely to run the detector above and has no UI. Treatment with nothing to
  // recommend would be an empty modal, and the server already refuses to bucket that case.
  if (!showModal || relatedWorks.length === 0) return null;

  return (
    <PromoDialog
      open={open}
      // The native dialog collapses backdrop clicks and Esc into one event; the x below reports
      // itself separately.
      onClose={() => close('outside')}
      showHeader={false}
      aria-label="Before you go"
    >
      <ModalContent>
        <Sheet>
          <SheetCloseButton
            iconSize={24}
            aria-label="Close"
            onClick={() => close('close_button')}
          />
          <Center>
            <Heading size="xs" as="h2">
              Didn't find what you're looking for?
            </Heading>
            <Body size="sm">Try one of these papers instead:</Body>
          </Center>
          <Cards>
            {relatedWorks.map((work, position) => (
              <Card key={work.id} className="js-loswp-exit-intent-card">
                <CardInfo>
                  <CardTitle>{work.title}</CardTitle>
                  <CardMetadata>
                    {work.authors.map((author, index) => (
                      <React.Fragment key={author.id}>
                        <BodyLink size="sm" href={author.href} rel={author.rel}>
                          {author.displayName}
                        </BodyLink>
                        {index < work.authors.length - 1 && <span>, </span>}
                      </React.Fragment>
                    ))}
                  </CardMetadata>
                  {work.publicationData && (
                    <Body size="xs">{work.publicationData}</Body>
                  )}
                </CardInfo>
                <CardCtas>
                  {work.hasDownloadableAttachment && (
                    <Button
                      size="sm"
                      icon="download"
                      onClick={() => onDownload(work, position)}
                    >
                      Download PDF
                    </Button>
                  )}
                  <ButtonLink
                    size="sm"
                    secondary
                    href={work.url}
                    rel={work.nofollow ? 'nofollow' : undefined}
                    onClick={() =>
                      record('view_pdf_click', {
                        relatedWorkId: work.id,
                        position,
                      })
                    }
                    trackClick={{
                      source: VIEW_SOURCE,
                      bucket: designVariant,
                      entityId: work.id,
                      extra: { source_work_id: workId, position },
                    }}
                  >
                    View PDF
                  </ButtonLink>
                </CardCtas>
              </Card>
            ))}
          </Cards>
        </Sheet>
      </ModalContent>
    </PromoDialog>
  );
};

export default LoswpExitIntentModal;
